/**
 * Coups de pouce — Attendu 1071 « Construire le symétrique, par rapport à un
 * axe, d'un point, d'une figure, sur feuille blanche ».
 *
 * trace_qcm_unique : les deux vérifications sur la figure (direction :
 * perpendiculaire à l'axe ; distance : même longueur, de l'autre côté), le
 * codage fait foi. L'exemple (revue 2026-09-04) oppose le candidat décalé à la
 * bonne distance et le vrai symétrique. Autres lettres (R, K) que le gen (M, H).
 * lecture_qcm_unique + lecture_saisie + lecture_tableau_vf : entrée PARTAGÉE —
 * les faits que porte une construction juste (H milieu, (d) médiatrice,
 * MM′ = 2·HM, point de l'axe fixe, longueurs conservées). Lettres N, K, Q.
 * SVG pas à pas pilotés par les flèches de la sheet (data-etapes / data-caption).
 *
 * Sans coup de pouce (pur rappel) : gestes_qcm_unique, gestes_texte_trou,
 * gestes_tableau_vf.
 */

const SVG_TRACE = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 360 250" width="100%" style="max-width:380px;height:auto;display:block" role="img" data-etapes="3"
     aria-label="Reconnaître le bon tracé du symétrique, en 3 étapes à faire défiler avec les flèches. Une droite (d), un point R à 2 cm, deux candidats R1 et R2 de l'autre côté, tous deux à 2 cm de (d). Étape 2, la direction : la droite (RR2) est perpendiculaire à (d), l'angle droit est codé en K ; la droite (RR1) n'est pas perpendiculaire à (d). Étape 3, la distance : KR2 égale KR égale 2 cm, de l'autre côté ; R1 est aussi à 2 cm mais décalé, ce n'est pas le symétrique. Perpendiculaire et même distance : R2">
  <style>
    @keyframes cp1071-pop { from { opacity: 0; } to { opacity: 1; } }
    .cp1071-pop { opacity: 0; animation: cp1071-pop 0.5s ease forwards; }
  </style>
  <!-- sous-titres : un par étape (exclusifs, pilotés par la sheet) -->
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#1E3A8A" opacity="1" data-caption="1" font-family="system-ui,sans-serif">Lequel est le symétrique de R par rapport à (d) ?</text>
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#1D9E75" opacity="0" data-caption="2" font-family="system-ui,sans-serif">Direction : une seule perpendiculaire</text>
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#1D9E75" opacity="0" data-caption="3" font-family="system-ui,sans-serif">Distance : même longueur, autre côté</text>

  <!-- ── La figure (étape 1) : axe oblique, R, deux candidats à 2 cm ──────── -->
  <g data-etape="1" font-family="system-ui,sans-serif">
    <line x1="14.0" y1="173.0" x2="166.0" y2="97.0" stroke="#DC2626" stroke-width="2.2"/>
    <text x="161.0" y="91.0" font-size="12" font-weight="700" fill="#DC2626" text-anchor="middle">(d)</text>
    <!-- perpendiculaire issue de R, pied K, jusqu'à R₂ -->
    <line x1="66.8" y1="110.9" x2="95.4" y2="168.1" stroke="#64748b" stroke-width="1.6" stroke-dasharray="5 3"/>
    <polyline points="87.4,136.4 84.2,130.1 78.0,133.2" fill="none" stroke="#475569" stroke-width="1.4"/>
    <!-- perpendiculaire de pied K′ portant R₁ -->
    <line x1="125.8" y1="117.1" x2="140.1" y2="145.7" stroke="#64748b" stroke-width="1.6" stroke-dasharray="5 3"/>
    <polyline points="132.1,113.9 135.2,120.2 128.9,123.4" fill="none" stroke="#475569" stroke-width="1.4"/>
    <!-- longueurs écrites -->
    <text x="61.5" y="135.0" font-size="11" font-weight="700" fill="#1E3A8A" text-anchor="middle">2 cm</text>
    <text x="75.8" y="164.0" font-size="11" font-weight="700" fill="#B45309" text-anchor="middle">2 cm</text>
    <text x="146.5" y="129.0" font-size="11" font-weight="700" fill="#B45309" text-anchor="middle">2 cm</text>
    <!-- points -->
    <circle cx="66.8" cy="110.9" r="3.4" fill="#1E3A8A"/><text x="62.0" y="104.0" font-size="13" font-weight="700" fill="#1E3A8A" text-anchor="middle">R</text>
    <circle cx="81.1" cy="139.5" r="3" fill="#15803d"/><text x="69.0" y="153.0" font-size="12" font-weight="700" fill="#15803d" text-anchor="middle">K</text>
    <circle cx="125.8" cy="117.1" r="3" fill="#15803d"/><text x="120.0" y="111.0" font-size="12" font-weight="700" fill="#15803d" text-anchor="middle">K′</text>
    <circle cx="95.4" cy="168.1" r="3.4" fill="#B45309"/><text x="101.0" y="184.0" font-size="13" font-weight="700" fill="#B45309" text-anchor="middle">R₂</text>
    <circle cx="140.1" cy="145.7" r="3.4" fill="#B45309"/><text x="146.0" y="162.0" font-size="13" font-weight="700" fill="#B45309" text-anchor="middle">R₁</text>
  </g>

  <!-- ── La direction (étape 2) ─────────────────────────────────────────── -->
  <g data-etape="2" data-anim="1" font-family="system-ui,sans-serif">
    <g class="cp1071-pop">
      <line x1="66.8" y1="110.9" x2="95.4" y2="168.1" stroke="#1D9E75" stroke-width="2.6"/>
      <line x1="66.8" y1="110.9" x2="140.1" y2="145.7" stroke="#B45309" stroke-width="1.8" stroke-dasharray="3 2"/>
      <text x="186" y="60" font-size="11.5" font-weight="800" fill="#1E3A8A">Direction :</text>
      <text x="186" y="75" font-size="11.5" font-weight="700" fill="#1D9E75">(RR₂) ⊥ (d) ✓</text>
      <text x="186" y="90" font-size="11" font-weight="600" fill="#1D9E75">angle droit codé en K.</text>
      <text x="186" y="110" font-size="11.5" font-weight="700" fill="#B45309">(RR₁) n'est pas</text>
      <text x="186" y="125" font-size="11.5" font-weight="700" fill="#B45309">perpendiculaire à (d) ✗</text>
    </g>
  </g>

  <!-- ── La distance (étape 3) ──────────────────────────────────────────── -->
  <g data-etape="3" data-anim="1" font-family="system-ui,sans-serif">
    <g class="cp1071-pop">
      <line x1="69.5" y1="127.4" x2="78.5" y2="122.9" stroke="#1D9E75" stroke-width="2"/>
      <line x1="83.8" y1="156.0" x2="92.8" y2="151.5" stroke="#1D9E75" stroke-width="2"/>
      <text x="186" y="160" font-size="11.5" font-weight="800" fill="#1E3A8A">Distance :</text>
      <text x="186" y="175" font-size="11.5" font-weight="700" fill="#1D9E75">KR₂ = KR = 2 cm,</text>
      <text x="186" y="190" font-size="11.5" font-weight="700" fill="#1D9E75">de l'autre côté ✓</text>
      <text x="186" y="210" font-size="11.5" font-weight="700" fill="#B45309">R₁ est aussi à 2 cm, mais</text>
      <text x="186" y="225" font-size="11.5" font-weight="700" fill="#B45309">décalé : pas symétrique ✗</text>
      <text x="180" y="246" font-size="12" font-weight="800" fill="#1E3A8A" text-anchor="middle">Perpendiculaire ET même distance : R₂.</text>
    </g>
  </g>

  <text x="180" y="246" font-size="11" font-weight="700" fill="#b45309" text-anchor="middle" opacity="0" data-caption="1" font-family="system-ui,sans-serif">↓ Touche les flèches pour avancer, étape par étape ↓</text>
</svg>`.trim();

const AIDE_TRACE = {
    pages: [
        {
            titre: 'La règle',
            lignes: [
                "**Deux vérifications, dans cet ordre.** D'abord la direction : le segment qui joint M à M′ est-il **perpendiculaire** à (d) ? Le codage de l'angle droit le dit.",
                "Ensuite la distance : M′ est-il à la **même distance** de (d) que M, et **de l'autre côté** ? Les longueurs écrites ou les codages le disent.",
                "**Il faut les deux.** Un point à la bonne distance mais hors de la perpendiculaire n'est pas le symétrique. Un point sur la perpendiculaire à une autre distance non plus.",
                "**Du même côté, jamais** : le symétrique traverse l'axe.",
                '**Vérification finale** : en pliant la feuille le long de (d), M tombe exactement sur M′.',
            ],
        },
        { titre: 'Un exemple', svg: SVG_TRACE },
    ],
};

const SVG_LECTURE = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 360 250" width="100%" style="max-width:380px;height:auto;display:block" role="img" data-etapes="3"
     aria-label="Lire une construction de symétrique, en 3 étapes à faire défiler avec les flèches. Une droite (d), un point N à 3 cm, la perpendiculaire à (d) issue de N de pied K, prolongée jusqu'à N′ de l'autre côté, et un point Q sur (d). Étape 2 : K est le milieu du segment NN′, KN′ égale KN égale 3 cm, et (d) est la médiatrice de NN′, perpendiculaire et passant par le milieu. Étape 3 : NN′ égale 3 plus 3 égale 6 cm, le double de KN ; Q est sur (d), son symétrique est Q lui-même">
  <style>
    @keyframes cp1071-pop { from { opacity: 0; } to { opacity: 1; } }
    .cp1071-pop { opacity: 0; animation: cp1071-pop 0.5s ease forwards; }
  </style>
  <!-- sous-titres : un par étape (exclusifs, pilotés par la sheet) -->
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#1E3A8A" opacity="1" data-caption="1" font-family="system-ui,sans-serif">N′ est le symétrique de N : que peut-on lire ?</text>
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#1D9E75" opacity="0" data-caption="2" font-family="system-ui,sans-serif">K : milieu de [NN′], (d) médiatrice</text>
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#1D9E75" opacity="0" data-caption="3" font-family="system-ui,sans-serif">Les longueurs, et le point de l'axe</text>

  <!-- ── La figure (étape 1) : axe oblique, N, K, N′, Q ────────────────────── -->
  <g data-etape="1" font-family="system-ui,sans-serif">
    <line x1="14.0" y1="173.0" x2="166.0" y2="97.0" stroke="#DC2626" stroke-width="2.2"/>
    <text x="161.0" y="91.0" font-size="12" font-weight="700" fill="#DC2626" text-anchor="middle">(d)</text>
    <line x1="55.1" y1="98.8" x2="98.1" y2="184.6" stroke="#64748b" stroke-width="1.6" stroke-dasharray="5 3"/>
    <polyline points="82.9,138.6 79.8,132.3 73.5,135.4" fill="none" stroke="#475569" stroke-width="1.4"/>
    <text x="53.4" y="130.0" font-size="11" font-weight="700" fill="#1E3A8A" text-anchor="middle">3 cm</text>
    <circle cx="55.1" cy="98.8" r="3.4" fill="#1E3A8A"/><text x="50.0" y="93.0" font-size="13" font-weight="700" fill="#1E3A8A" text-anchor="middle">N</text>
    <circle cx="76.6" cy="141.7" r="3" fill="#15803d"/><text x="67.0" y="155.0" font-size="12" font-weight="700" fill="#15803d" text-anchor="middle">K</text>
    <circle cx="98.1" cy="184.6" r="3.4" fill="#B45309"/><text x="104.0" y="199.0" font-size="13" font-weight="700" fill="#B45309" text-anchor="middle">N′</text>
    <circle cx="139.2" cy="110.4" r="3.4" fill="#15803d"/><text x="134.0" y="104.0" font-size="13" font-weight="700" fill="#15803d" text-anchor="middle">Q</text>
  </g>

  <!-- ── Le milieu et la médiatrice (étape 2) ───────────────────────────── -->
  <g data-etape="2" data-anim="1" font-family="system-ui,sans-serif">
    <g class="cp1071-pop">
      <circle cx="76.6" cy="141.7" r="6" fill="none" stroke="#1D9E75" stroke-width="2"/>
      <line x1="61.4" y1="122.5" x2="70.4" y2="118.1" stroke="#1D9E75" stroke-width="2"/>
      <line x1="82.9" y1="165.4" x2="91.9" y2="161.0" stroke="#1D9E75" stroke-width="2"/>
      <text x="74.9" y="173.0" font-size="11" font-weight="700" fill="#B45309" text-anchor="middle">3 cm</text>
      <text x="186" y="60" font-size="11.5" font-weight="700" fill="#1D9E75">K est le milieu de [NN′] :</text>
      <text x="186" y="75" font-size="11.5" font-weight="700" fill="#1D9E75">KN′ = KN = 3 cm.</text>
      <text x="186" y="95" font-size="11.5" font-weight="700" fill="#1E3A8A">(d) est la médiatrice</text>
      <text x="186" y="110" font-size="11.5" font-weight="700" fill="#1E3A8A">de [NN′] : perpendiculaire,</text>
      <text x="186" y="125" font-size="11.5" font-weight="700" fill="#1E3A8A">par le milieu.</text>
    </g>
  </g>

  <!-- ── Les longueurs et le point de l'axe (étape 3) ───────────────────── -->
  <g data-etape="3" data-anim="1" font-family="system-ui,sans-serif">
    <g class="cp1071-pop">
      <line x1="74.8" y1="89.0" x2="117.8" y2="174.8" stroke="#1D9E75" stroke-width="1.6"/>
      <line x1="70.3" y1="91.2" x2="79.3" y2="86.8" stroke="#1D9E75" stroke-width="1.6"/>
      <line x1="113.3" y1="177.0" x2="122.3" y2="172.6" stroke="#1D9E75" stroke-width="1.6"/>
      <text x="112.0" y="162.0" font-size="11" font-weight="700" fill="#1D9E75">NN′ = 6 cm</text>
      <circle cx="139.2" cy="110.4" r="7" fill="none" stroke="#1D9E75" stroke-width="2" stroke-dasharray="3 2"/>
      <text x="186" y="160" font-size="11.5" font-weight="700" fill="#1D9E75">NN′ = 3 + 3 = 6 cm :</text>
      <text x="186" y="175" font-size="11.5" font-weight="700" fill="#1D9E75">le double de KN.</text>
      <text x="186" y="195" font-size="11.5" font-weight="700" fill="#1E3A8A">Q est sur (d) : son</text>
      <text x="186" y="210" font-size="11.5" font-weight="700" fill="#1E3A8A">symétrique est Q lui-même.</text>
      <text x="180" y="246" font-size="12" font-weight="800" fill="#1E3A8A" text-anchor="middle">NN′ = 2 × 3 cm, et Q ne bouge pas.</text>
    </g>
  </g>

  <text x="180" y="246" font-size="11" font-weight="700" fill="#b45309" text-anchor="middle" opacity="0" data-caption="1" font-family="system-ui,sans-serif">↓ Touche les flèches pour avancer, étape par étape ↓</text>
</svg>`.trim();

const AIDE_LECTURE = {
    pages: [
        {
            titre: 'La règle',
            lignes: [
                "**Ce que dit une construction juste** : la droite (MM′) est perpendiculaire à (d), et le pied H de la perpendiculaire est le **milieu** de [MM′].",
                "Donc **HM = HM′**, et la longueur MM′ vaut **le double** de la distance de M à l'axe. Dans l'autre sens, la distance HM′ vaut **la moitié** de MM′.",
                "**(d) est la médiatrice de [MM′]** : perpendiculaire au segment et passant par son milieu.",
                "**Un point de l'axe ne bouge pas** : il est son propre symétrique. Un segment qui coupe (d) en I a un symétrique qui passe aussi par I.",
                "**Les longueurs sont conservées** : un côté de 4 cm a un symétrique de 4 cm, quelle que soit la figure.",
            ],
        },
        { titre: 'Un exemple', svg: SVG_LECTURE },
    ],
};

const CP_1071 = {
    trace_qcm_unique: AIDE_TRACE,
    lecture_qcm_unique: AIDE_LECTURE,
    lecture_saisie: AIDE_LECTURE,
    lecture_tableau_vf: AIDE_LECTURE,
};

export default CP_1071;
